import { useEffect, useRef, useState } from 'preact/hooks'
import { text } from '#contract'
import { useView } from '../app/use-view.ts'
import { useLocale, useT } from '../i18n/index.ts'
import { formatMoney } from '../i18n/money.ts'
import styles from './wallet-badge.module.css'

/** How long a change keeps its tint, in ms. */
const TINT_MS = 1000

/**
 * The player's balance (`view.wallet`), small under the clock. A change pulses it once,
 * tinted for 1 s: gain or loss.
 */
export function WalletBadge() {
  const t = useT()
  const locale = useLocale()
  const wallet = useView((view) => view.wallet)
  const cents = wallet?.cents ?? null
  const [change, setChange] = useState<{ readonly gain: boolean; readonly n: number } | null>(null)
  // The balance last shown: a change while the HUD was hidden doesn't pulse when it shows.
  const lastRef = useRef(cents)
  useEffect(() => {
    const prev = lastRef.current
    lastRef.current = cents
    if (cents === null || prev === null || cents === prev) return
    setChange((current) => ({ gain: cents > prev, n: (current?.n ?? 0) + 1 }))
    const timer = setTimeout(() => {
      setChange(null)
    }, TINT_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [cents])
  if (!wallet) return null

  const money = formatMoney(wallet.cents, wallet.currency, locale)
  return (
    <data
      key={change?.n}
      class={styles.wallet}
      value={wallet.cents}
      aria-label={t(text('wallet.label', { money }))}
      data-change={change ? (change.gain ? 'gain' : 'loss') : undefined}
    >
      {money}
    </data>
  )
}
